import { useState, useCallback } from 'react'
import { apiClient } from '@/lib/api-client'
import type {
  ConjuntoResponse,
  CreateConjuntoRequest,
  UpdateConjuntoRequest,
  ExportResponse,
  ConjuntoVersionResponse,
} from '@/lib/api-types'
import { useToast } from '@/hooks/use-toast'

export interface UseConjuntoResult {
  conjunto: ConjuntoResponse | null
  loading: boolean
  error: string | null
  exporting: boolean
  createConjunto: (data: CreateConjuntoRequest) => Promise<ConjuntoResponse | null>
  updateConjunto: (id: string, data: UpdateConjuntoRequest) => Promise<ConjuntoResponse | null>
  getConjunto: (id: string) => Promise<ConjuntoResponse | null>
  exportConjunto: (id: string) => Promise<ExportResponse | null>
  getVersions: (id: string) => Promise<ConjuntoVersionResponse[]>
}

export function useConjunto(): UseConjuntoResult {
  const [conjunto, setConjunto] = useState<ConjuntoResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { toast } = useToast()

  // Helper para extrair mensagem de erro com segurança
  const getErrorMessage = (err: any): string => {
    if (typeof err === 'string') return err
    if (err?.response?.data?.detail) {
      const detail = err.response.data.detail
      return typeof detail === 'string' ? detail : JSON.stringify(detail)
    }
    if (err?.message) return err.message
    if (err?.error) return String(err.error)
    return 'Erro desconhecido'
  }

  const createConjunto = useCallback(
    async (data: CreateConjuntoRequest): Promise<ConjuntoResponse | null> => {
      try {
        setLoading(true)
        setError(null)
        const response = await apiClient.createConjunto(data)
        setConjunto(response.data)
        toast({
          title: 'Sucesso',
          description: 'Conjunto criado com sucesso',
        })
        return response.data
      } catch (err: any) {
        const msg = getErrorMessage(err)
        setError(msg)
        toast({
          title: 'Erro',
          description: msg,
          variant: 'destructive',
        })
        return null
      } finally {
        setLoading(false)
      }
    },
    [toast]
  )

  const updateConjunto = useCallback(
    async (
      id: string,
      data: UpdateConjuntoRequest
    ): Promise<ConjuntoResponse | null> => {
      try {
        const response = await apiClient.updateConjunto(id, data)
        setConjunto(response.data)
        return response.data
      } catch (err: any) {
        const msg = getErrorMessage(err)
        setError(msg)
        toast({
          title: 'Erro',
          description: msg,
          variant: 'destructive',
        })
        return null
      }
    },
    [toast]
  )

  const getConjunto = useCallback(
    async (id: string): Promise<ConjuntoResponse | null> => {
      try {
        setLoading(true)
        const response = await apiClient.getConjunto(id)
        setConjunto(response.data)
        return response.data
      } catch (err: any) {
        const msg = getErrorMessage(err)
        setError(msg)
        toast({
          title: 'Erro',
          description: msg,
          variant: 'destructive',
        })
        return null
      } finally {
        setLoading(false)
      }
    },
    [toast]
  )

  const exportConjunto = useCallback(
    async (id: string): Promise<ExportResponse | null> => {
      try {
        setExporting(true)
        setError(null)
        const response = await apiClient.exportConjunto(id)
        toast({
          title: 'Sucesso',
          description: 'Conjunto exportado com sucesso!',
        })
        return response.data
      } catch (err: any) {
        const msg = getErrorMessage(err)
        setError(msg)
        toast({
          title: 'Erro',
          description: msg,
          variant: 'destructive',
        })
        return null
      } finally {
        setExporting(false)
      }
    },
    [toast]
  )

  const getVersions = useCallback(
    async (id: string): Promise<ConjuntoVersionResponse[]> => {
      try {
        const response = await apiClient.listVersions(id)
        return response.data
      } catch (err: any) {
        const msg = getErrorMessage(err)
        toast({
          title: 'Erro',
          description: msg,
          variant: 'destructive',
        })
        return []
      }
    },
    [toast]
  )

  return {
    conjunto,
    loading,
    error,
    exporting,
    createConjunto,
    updateConjunto,
    getConjunto,
    exportConjunto,
    getVersions,
  }
}


